import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DeprecatedConfirmationDialogComponent, DeprecatedModalComponent, PdfViewerComponent } from '@app/components';
import { AnalyticEvents, TaskTypes } from '@app/enums';
import { getAllFileTypes } from '@app/functions/files/get-all-file-types';
import { getImageFileTypes } from '@app/functions/files/get-image-file-types';
import { validateForm } from '@app/functions/validate-form';
import { File } from '@app/models/common/file.model';
import { Employee } from '@app/models/employee/employee.model';
import { TaskSubtask } from '@app/models/tasks/task-subtask.model';
import { Task } from '@app/models/tasks/task.model';
import { AnalyticService, AuthService, FileHelperService, NotifyService, TasksService } from '@app/services';
import { TaskDetailsComponent } from '../task-details/task-details.component';

@Component({
    selector: 'app-task-checklist',
    templateUrl: './task-checklist.template.html',
    styleUrls: ['./task-checklist.style.scss'],
})
export class TaskChecklistComponent {
    @ViewChild('confirmTaskDeleteDialog', { static: true })
    confirmTaskDeleteDialog: DeprecatedConfirmationDialogComponent;
    @ViewChild('taskDetailsModal') taskDetailsModal: DeprecatedModalComponent;
    @ViewChild('taskDetails', { static: true }) taskDetails: TaskDetailsComponent;
    @ViewChild('container') container: ElementRef;
    @ViewChild('pdfPreviewModal', { static: true }) pdfPreviewModal: DeprecatedModalComponent;
    @ViewChild('pdfViewer', { static: true }) pdfPreview: PdfViewerComponent;

    @Output() updateTasks: EventEmitter<void> = new EventEmitter<void>();
    @Output() showTaskModal: EventEmitter<Task> = new EventEmitter<Task>();
    @Output() onScroll: EventEmitter<void> = new EventEmitter<void>();

    @Output() reminded: EventEmitter<void> = new EventEmitter<void>();
    @Output() deleted: EventEmitter<void> = new EventEmitter<void>();
    @Output() viewed: EventEmitter<void> = new EventEmitter<void>();

    @Input() tasks: Task[] = [];
    @Input() employee: Employee;
    @Input() showTaskModalOnEdit = false;
    @Input() onDashboard = false;
    @Input() showTaskId: number = null;
    @Input() viewTaskDetailsAnalyticEvent: AnalyticEvents;

    currentTask: Task;
    isAdmin = false;
    allowedImgTypes = getImageFileTypes();
    allowedFileTypes = getAllFileTypes();

    taskTypes = TaskTypes;
    defaultTaskDetailAnalyticEvent = AnalyticEvents.EmployeeProfileViewTaskDetailsModal;

    constructor(
        public analyticService: AnalyticService,
        private tasksService: TasksService,
        private router: Router,
        protected route: ActivatedRoute,
        private notify: NotifyService,
        private auth: AuthService,
        private fileHelper: FileHelperService
    ) {
        if (this.auth.isAdmin()) {
            this.isAdmin = true;
        }
    }

    async ngOnInit(): Promise<void> {
        if (this.showTaskId) {
            this.onViewTaskDetails(await Task.find(this.showTaskId));
        }
    }

    onRemind(task: Task): void {
        this.tasksService.sendReminder(task).then(() => this.reminded.emit());
    }

    onEdit(task: Task, event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.showTaskModalOnEdit) {
            this.showTaskModal.emit(task);
            return;
        }
        this.router.navigate(['/employees', 'directory', task.subjectId, 'tasks', task.id, 'edit']);
    }

    onViewTaskDetails(task: Task): void {
        if (!this.showTaskId) {
            this.analyticService.trackEvent(this.viewTaskDetailsAnalyticEvent ?? this.defaultTaskDetailAnalyticEvent);
        }

        this.viewed.emit();
        this.currentTask = task;

        this.taskDetails.beforeShow(task);
        this.taskDetailsModal.show();
    }

    onClose(): void {
        this.taskDetailsModal.onClose();
    }

    onCancel(): void {}

    onDelete(task: Task): void {
        this.setTaskDeleteDialogHeaderAndDescription(task);

        this.confirmTaskDeleteDialog.show().then((confirm) => {
            if (!confirm) {
                return;
            }

            this.tasksService.deleteTask(task, this.employee).then(() => {
                this.notify.success('employees.task-checklist.taskHasBeenDeleted');
                this.updateTasks.emit();
                this.deleted.emit();
            });
        });
    }

    onFileSelected(files: FileList, task: Task): void {
        this.tasksService.attachFile(files, task).then(() => {
            this.updateTasks.emit();
        });
    }

    onFileRemoved(task: Task): void {
        if (task.isComplete()) {
            task.completedAt = null;
        }
        this.tasksService.removeFile(task).then(() => {
            this.updateTasks.emit();
        });
    }

    onToggleTaskComplete(event: MouseEvent, task: Task, form: NgForm): void {
        validateForm(form);
        if (form.invalid) {
            (event.target as HTMLInputElement).checked = task.isComplete();
            return;
        }

        this.tasksService.toggleCompletion(task);
    }

    onScrollContainer(): void {
        this.onScroll.emit();
    }

    async viewFile(file: File): Promise<void> {
        // If it's an image, download this file for local viewing
        if (this.allowedImgTypes.includes(`.${file.type}`)) {
            this.fileHelper.saveFile(file);
            return;
        }

        // otherwise, use pdf viewer
        await this.pdfPreview.setSource(this.fileHelper.path(file.id));
        this.pdfPreviewModal.show();
    }

    getCompletedSubtasksCount(task: Task): number {
        return task.subtasks.filter((subTask: TaskSubtask) => subTask.completedAt).length;
    }

    private setTaskDeleteDialogHeaderAndDescription(task: Task): void {
        this.confirmTaskDeleteDialog.description = {
            key: 'employees.task-checklist.deleteConfirmationMessage',
            params: { taskName: task.name },
        };

        switch (task.taskType) {
            case this.taskTypes.onboarding:
                this.confirmTaskDeleteDialog.header = {
                    key: 'employees.task-checklist.deleteConfirmationTitle',
                    params: {
                        firstName: this.employee.firstName,
                        taskType: 'Onboarding',
                    },
                };
                break;
            case this.taskTypes.offboarding:
                this.confirmTaskDeleteDialog.header = {
                    key: 'employees.task-checklist.deleteConfirmationTitle',
                    params: {
                        firstName: this.employee.firstName,
                        taskType: 'Offboarding',
                    },
                };
                break;
            case this.taskTypes.general:
                this.confirmTaskDeleteDialog.header = {
                    key: 'employees.task-checklist.deleteConfirmationTitle',
                    params: {
                        firstName: this.employee.firstName,
                        taskType: 'General',
                    },
                };
                break;
        }
    }
}
